import React, { useEffect, useRef, useState } from "react";
import { Music, Upload, Trash2, Play, Pause, Loader2, Star, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl } from "@/lib/uploads";
import { useI18n } from "@/context/I18nContext";
import { RINGTONE_CATEGORIES, loadRingtones } from "@/lib/sound";
import { toast } from "sonner";

const ACCEPT = ".mp3,.wav,.ogg,audio/mpeg,audio/wav,audio/ogg";
const fmtDur = (s) => { if (!s || s < 0) return "—"; const m = Math.floor(s / 60); const ss = Math.round(s % 60); return `${m}:${ss.toString().padStart(2, "0")}`; };

// Read audio duration (seconds) from a File without uploading.
const readDuration = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const a = new Audio();
    a.preload = "metadata";
    a.onloadedmetadata = () => { const d = a.duration; URL.revokeObjectURL(url); resolve(Number.isFinite(d) ? d : 0); };
    a.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
    a.src = url;
  } catch { resolve(0); }
});

export default function RingtonesAdmin() {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const catLabel = (key) => { const c = RINGTONE_CATEGORIES.find((x) => x.key === key); return c ? (fr ? c.labelFr : c.label) : key; };

  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("incoming_audio");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(null); // {ok, msg}
  const [playingId, setPlayingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/ringtones"); setItems(data || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }; }, []);

  const doUpload = async (file) => {
    if (!file) return;
    if (!/\.(mp3|wav|ogg)$/i.test(file.name)) { setStatus({ ok: false, msg: fr ? "Format non pris en charge (.mp3, .wav, .ogg)" : "Unsupported format (.mp3, .wav, .ogg)" }); return; }
    setBusy(true); setProgress(0); setStatus(null);
    try {
      const dur = await readDuration(file);
      const up = await uploadFileChunked(file, setProgress, "ringtone");
      const fileId = up.url.split("/api/files/")[1];
      const name = file.name.replace(/\.(mp3|wav|ogg)$/i, "");
      const { data } = await api.post("/admin/ringtones", {
        file_id: fileId, name, category, size: file.size, content_type: up.content_type || file.type, duration: dur,
      });
      setItems((cur) => [data, ...(cur || [])]);
      setStatus({ ok: true, msg: (fr ? "Sonnerie ajoutée : " : "Ringtone added: ") + data.name });
      loadRingtones(true);
    } catch (e) {
      setStatus({ ok: false, msg: e?.response?.data?.detail || (fr ? "Échec du téléversement" : "Upload failed") });
    } finally { setBusy(false); setProgress(0); }
  };

  const togglePlay = (rt) => {
    if (playingId === rt.id) {
      if (audioRef.current) { audioRef.current.pause(); }
      setPlayingId(null); return;
    }
    if (audioRef.current) { audioRef.current.pause(); }
    const a = new Audio(fileUrl(rt.url));
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    a.play().catch(() => {});
    setPlayingId(rt.id);
  };

  const rename = async (rt, name) => {
    const clean = (name || "").trim();
    if (!clean || clean === rt.name) return;
    try { const { data } = await api.put(`/admin/ringtones/${rt.id}`, { name: clean }); setItems((cur) => cur.map((x) => x.id === rt.id ? data : x)); loadRingtones(true); }
    catch { toast.error(fr ? "Échec du renommage" : "Rename failed"); }
  };
  const changeCat = async (rt, cat) => {
    try { const { data } = await api.put(`/admin/ringtones/${rt.id}`, { category: cat }); setItems((cur) => cur.map((x) => x.id === rt.id ? data : x)); loadRingtones(true); }
    catch { toast.error(fr ? "Échec" : "Failed"); }
  };
  const setDefault = async (rt) => {
    try {
      const { data } = await api.put(`/admin/ringtones/${rt.id}`, { is_default: !rt.is_default });
      setItems((cur) => cur.map((x) => x.category === rt.category ? { ...x, is_default: x.id === rt.id ? data.is_default : false } : x));
      loadRingtones(true);
      toast.success(data.is_default ? (fr ? "Défini par défaut" : "Set as default") : (fr ? "Défaut retiré" : "Default removed"));
    } catch { toast.error(fr ? "Échec" : "Failed"); }
  };
  const remove = async (id) => {
    setConfirmId(null);
    try { await api.delete(`/admin/ringtones/${id}`); setItems((cur) => cur.filter((x) => x.id !== id)); if (playingId === id && audioRef.current) audioRef.current.pause(); loadRingtones(true); toast.success(fr ? "Sonnerie supprimée" : "Ringtone deleted"); }
    catch { toast.error(fr ? "Échec de la suppression" : "Delete failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-ringtones">
      <p className="text-sm text-[var(--rc-muted)]">{fr
        ? "Gérez les sonneries d'appels et de messages. Les utilisateurs choisiront parmi cette liste dans leurs réglages."
        : "Manage call & message ringtones. Users pick from this list in their settings."}</p>

      {/* Upload */}
      <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3">
        <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)]">{fr ? "AJOUTER UNE SONNERIE" : "ADD A RINGTONE"}</p>
        <div>
          <label className="text-xs text-[var(--rc-muted)]">{fr ? "Catégorie" : "Category"}</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ringtone-upload-category"
            className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 mt-1 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50">
            {RINGTONE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{fr ? c.labelFr : c.label}</option>)}
          </select>
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="ringtone-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doUpload(f); }} />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ringtone-upload-btn"
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">
          {busy ? <><Loader2 size={16} className="animate-spin" /> {progress}%</> : <><Upload size={16} /> {fr ? "Choisir un fichier (.mp3, .wav, .ogg)" : "Choose a file (.mp3, .wav, .ogg)"}</>}
        </button>
        {busy && (
          <div className="h-1.5 w-full rounded-full bg-[var(--rc-border)] overflow-hidden">
            <div className="h-full bg-[var(--rc-gold)] transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        {status && (
          <div data-testid="ringtone-upload-status" className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${status.ok ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"}`}>
            {status.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {status.msg}
          </div>
        )}
      </div>

      {/* List */}
      <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--rc-muted)] border-b border-[var(--rc-border)]">
          <span>{fr ? "Nom / Catégorie" : "Name / Category"}</span>
          <span className="text-center w-14">{fr ? "Durée" : "Duration"}</span>
          <span className="text-center w-10">{fr ? "Écouter" : "Play"}</span>
          <span className="text-center w-16">{fr ? "Défaut" : "Default"}</span>
        </div>
        {items === null ? (
          <div className="flex items-center gap-2 text-sm text-[var(--rc-muted)] p-4"><Loader2 size={16} className="animate-spin" /> …</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[var(--rc-muted)] text-center py-8" data-testid="ringtones-empty">{fr ? "Aucune sonnerie pour l'instant." : "No ringtones yet."}</div>
        ) : (
          items.map((rt) => (
            <div key={rt.id} data-testid={`ringtone-row-${rt.id}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-4 py-3 border-b border-[var(--rc-border)] last:border-b-0">
              <div className="min-w-0">
                <input defaultValue={rt.name} onBlur={(e) => rename(rt, e.target.value)} data-testid={`ringtone-name-${rt.id}`}
                  className="w-full bg-transparent border-b border-transparent hover:border-[var(--rc-border)] focus:border-[var(--rc-gold)]/60 text-sm font-medium py-1 focus:outline-none" />
                <select value={rt.category} onChange={(e) => changeCat(rt, e.target.value)} data-testid={`ringtone-cat-${rt.id}`}
                  className="mt-1 bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-lg px-2 py-1 text-[11px] text-[var(--rc-muted)] focus:outline-none">
                  {RINGTONE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{fr ? c.labelFr : c.label}</option>)}
                </select>
              </div>
              <span className="text-center w-14 text-xs text-[var(--rc-muted)]">{fmtDur(rt.duration)}</span>
              <button onClick={() => togglePlay(rt)} data-testid={`ringtone-play-${rt.id}`}
                className="w-10 h-9 grid place-items-center rounded-lg border border-[var(--rc-border)] text-[var(--rc-gold)] hover:bg-[var(--rc-gold)]/10 mx-auto">
                {playingId === rt.id ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <div className="w-16 flex items-center justify-center gap-1">
                <button onClick={() => setDefault(rt)} data-testid={`ringtone-default-${rt.id}`} title={fr ? "Par défaut" : "Default"}
                  className={`h-9 w-9 grid place-items-center rounded-lg border ${rt.is_default ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>
                  <Star size={15} fill={rt.is_default ? "currentColor" : "none"} />
                </button>
                <button onClick={() => setConfirmId(rt.id)} data-testid={`ringtone-delete-${rt.id}`}
                  className="h-9 w-9 grid place-items-center rounded-lg border border-[var(--rc-border)] text-[var(--rc-muted)] hover:text-red-400 hover:border-red-500/40">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {confirmId && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-5" onClick={() => setConfirmId(null)} data-testid="ringtone-delete-confirm">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-5 rc-pop">
            <div className="flex items-center gap-3 mb-2">
              <span className="h-10 w-10 grid place-items-center rounded-full bg-red-500/15 text-red-400 shrink-0"><AlertTriangle size={20} /></span>
              <div className="font-heading text-xl">{fr ? "Supprimer la sonnerie ?" : "Delete ringtone?"}</div>
            </div>
            <p className="text-sm text-[var(--rc-muted)] mb-5">{fr ? "Cette action est définitive." : "This cannot be undone."}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmId(null)} data-testid="ringtone-delete-cancel" className="flex-1 rounded-full border border-[var(--rc-border)] py-2.5 font-medium">{fr ? "Annuler" : "Cancel"}</button>
              <button onClick={() => remove(confirmId)} data-testid="ringtone-delete-confirm-btn" className="flex-1 rounded-full bg-red-500 text-white py-2.5 font-semibold">{fr ? "Supprimer" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
